// Coalesces avatar hydration fetches into batched POST /users/bulk calls.
// A feed renders dozens of UserIcons in one commit; icons whose user payload
// lacks avatar_urls each used to issue their own single-id bulk fetch. Queueing
// ids for a tick collapses that burst into one request.
import {
    cacheUser,
    isUserFetchPending,
    markUserFetchPending,
    clearUserFetchPending,
} from 'state/cache'

const FLUSH_DELAY_MS = 16
const MAX_BATCH_SIZE = 100
// Keep ids marked pending briefly after a flush so unmount/remount churn does
// not immediately re-request users that just resolved (matches the delay the
// previous per-icon fetch used).
const CLEAR_PENDING_DELAY_MS = 5000

const queuedIds = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

function flushUserAvatarHydrationQueue() {
    flushTimer = null
    const ids = Array.from(queuedIds)
    queuedIds.clear()
    if (ids.length === 0) return

    for (let index = 0; index < ids.length; index += MAX_BATCH_SIZE) {
        const batchIds = ids.slice(index, index + MAX_BATCH_SIZE)

        fetch(`${window.knovApiUrl}/api/v1/users/bulk`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({ ids: batchIds }),
        })
            .then(r => (r.ok ? r.json() : null))
            .then(data => {
                for (const u of data?.users || []) {
                    if (!u?.id) continue
                    cacheUser(u, true)
                    // Reuse the avatar-update event so every mounted UserIcon for
                    // this user re-resolves and switches to direct avatar_urls.
                    try {
                        window.dispatchEvent(
                            new CustomEvent('userAvatarUpdated', { detail: { user: u } }),
                        )
                    } catch (e) {}
                }
            })
            .catch(() => {})
            .finally(() => {
                setTimeout(() => {
                    batchIds.forEach(id => clearUserFetchPending(id))
                }, CLEAR_PENDING_DELAY_MS)
            })
    }
}

export function queueUserAvatarHydration(userId: string | number | null | undefined) {
    const key = String(userId || '')
    if (!key) return
    if (isUserFetchPending(key)) return

    markUserFetchPending(key)
    queuedIds.add(key)
    if (!flushTimer) {
        flushTimer = setTimeout(flushUserAvatarHydrationQueue, FLUSH_DELAY_MS)
    }
}
